import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2, Clock, FileText, Star, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function DashboardHome() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/institution/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;

  const inst = data.institution;
  const statusColor = { approved: "text-green-700 bg-green-50", pending: "text-amber-700 bg-amber-50",
    rejected: "text-red-700 bg-red-50", suspended: "text-slate-700 bg-slate-100" }[inst.listing_status] || "bg-slate-100";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{inst.name}</h1>
          <p className="text-sm text-slate-500">Welcome to your institution dashboard</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${statusColor}`} data-testid="listing-status">
          {inst.listing_status}
        </span>
      </div>

      {inst.listing_status === "pending" && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          Your institution is pending admin approval. You can add courses now — they will go live once approved.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={BookOpen} label="Total Courses" value={data.total_courses} />
        <Stat icon={CheckCircle2} label="Published" value={data.published_courses} />
        <Stat icon={Clock} label="Pending" value={data.pending_courses} />
        <Stat icon={FileText} label="Drafts" value={data.draft_courses} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-blue-800" />
            <h3 className="font-heading font-semibold text-slate-900">Current Plan</h3>
          </div>
          <p className="mt-2 font-heading text-2xl font-bold capitalize text-slate-900">{data.plan}</p>
          {data.subscription_expiry && <p className="text-sm text-slate-500">Valid until {data.subscription_expiry.slice(0, 10)}</p>}
          {data.pending_downgrade_plan && <p className="mt-1 text-sm text-amber-700">Scheduled downgrade to {data.pending_downgrade_plan}</p>}
          <Button asChild className="mt-4 bg-blue-800 hover:bg-blue-900" size="sm"><Link to="/dashboard/listing">Manage Plan</Link></Button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading font-semibold text-slate-900">Quick Actions</h3>
          <div className="mt-3 flex flex-col gap-2">
            <Button asChild variant="outline" size="sm" className="justify-start"><Link to="/dashboard/courses">Add / Manage Courses</Link></Button>
            <Button asChild variant="outline" size="sm" className="justify-start"><Link to="/dashboard/institution">Edit Institution Profile</Link></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <Icon className="h-5 w-5 text-blue-800" />
      <p className="mt-2 font-heading text-2xl font-bold text-slate-900">{value ?? 0}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
